import '../person-card.less';

import ConditionalLink from '@plone/volto/components/manage/ConditionalLink/ConditionalLink';
import cx from 'classnames';
import { defineMessages, useIntl } from 'react-intl';

import { ContactInfo } from '../components/ContactInfo';
import { DocumentsCollapse } from '../components/DocumentsCollapse';
import { PersonImage } from '../components/PersonImage';
import { PrimaryAgendaButton } from '../components/PrimaryAgendaButton';
import { Substitute } from '../components/Substitute';
import { usePersonCard } from '../hooks/usePersonCard';

const messages = defineMessages({
  documentsTitle: {
    id: 'Documents',
    defaultMessage: 'Documents',
  },
  curriculumView: {
    id: 'View CV',
    // "Curriculum" alone is uncommon in English for a CV; prefer "View CV".
    defaultMessage: 'View CV',
  },
  linkNewTab: {
    id: 'person.link.newtab',
    defaultMessage: '{label} - opens in new tab',
  },
});
export const Vertical = (props) => {
  const intl = useIntl();
  const {
    person,
    summary,
    showDescription = true,
    hideName,
    showPersonLink,
    showAllLinks = true,
    showSubstitute: showSubstituteProp = true,
    isEditMode = false,
  } = props;

  // Hook deve ser chamado sempre (mesmo com person indefinido)
  const { primary, secondary, headingId } = usePersonCard(person, intl);

  if (!person) return null;

  const headingLevel = 2; // futuro: tornar prop configurável (headingLevel)
  const HeadingTag = `h${headingLevel}`;
  const personId = person?.UID || person?.id || person['@id']?.split('/').pop();
  // headingId já derivado pelo hook; fallback local se necessário
  const safeHeadingId = headingId || `person-${personId}-heading`;

  const showSubstitute = showSubstituteProp && !!person.substituto;
  const reviewStateClass =
    person?.review_state && person.review_state !== 'published'
      ? `review-state-${person.review_state}`
      : null;

  const classes = {
    card: cx(
      'br-card',
      'person-card',
      'person-card--vertical',
      'd-flex flex-column h-100',
      { 'is-edit-mode': isEditMode },
    ),
    headerInner: cx(
      'person-card__header-inner',
      'd-flex flex-column align-items-center gap-1',
    ),
    name: cx(
      'text-weight-medium text-center m-0',
      summary ? 'text-up-01' : 'text-up-03',
      reviewStateClass,
    ),
    cargo: cx(
      'text-weight-medium text-center m-0',
      summary ? 'text-base' : 'text-up-01',
    ),
    content: cx('person-card__content pt-0 d-flex flex-column flex-grow-1'),
    body: cx(
      'person-card__body',
      'd-flex flex-column flex-grow-1 w-100',
      // padding lateral moderado (menor que opção 2 anterior)
      'px-2x',
    ),
    bodyContent: cx('d-flex flex-column flex-grow-1'),
    bottomSection: cx('person-card__bottom-section', 'mt-auto'),
    description: cx('text-base text-center mt-2x px-2x', {
      'mb-2x': !showAllLinks && !showSubstitute, // neste caso ele é a ultima coisa
    }),
    contactGroup: cx(
      'person-card__contact-group',
      'w-100',
      summary ? 'mt-auto pt-base pb-base' : 'mt-2x',
      {
        'mb-2x': !showDescription && !showAllLinks && !showSubstitute, // neste caso ele é a ultima coisa
      },
    ),
    contactInline: cx(
      'd-flex flex-row flex-wrap gap-2 align-items-center justify-content-center',
      'mt-1',
    ),
    primaryAction: cx('person-card__primary-action mt-2x mb-2x'),
  };

  return (
    <article className={classes.card} aria-labelledby={safeHeadingId}>
      <div className="card-header person-card__header">
        <div className={classes.headerInner}>
          <PersonImage person={person} md={!!summary} sm={!!summary} />
          {!hideName && (
            <HeadingTag id={safeHeadingId} className={classes.name}>
              <ConditionalLink
                condition={showPersonLink && !isEditMode}
                href={person['@id']}
                className={reviewStateClass || undefined}
              >
                {showPersonLink && isEditMode ? (
                  <span className={cx('br-link-disabled', reviewStateClass)}>
                    {person.title}
                  </span>
                ) : (
                  person.title
                )}
              </ConditionalLink>
            </HeadingTag>
          )}
          {person.cargo && <div className={classes.cargo}>{person.cargo}</div>}
        </div>
      </div>
      <div className={cx(classes.content, 'flex-grow-1')}>
        <div className={classes.body}>
          <div className={classes.bodyContent}>
            {/* Ação primária destacada */}
            <div className={classes.primaryAction}>
              <PrimaryAgendaButton
                item={primary}
                label={primary?.label}
                isEditMode={isEditMode}
              />
            </div>

            {/* Contatos básicos em linha */}
            {!summary && (
              <div className={cx(classes.contactGroup)}>
                <div className={classes.contactInline}>
                  <ContactInfo
                    person={person}
                    variant="basic"
                    isEditMode={isEditMode}
                  />
                </div>
              </div>
            )}

            {/* Descrição entre o link primário e os demais links */}
            {showDescription && person.description && (
              <p
                className={classes.description}
                data-full-text={person.description}
              >
                {person.description}
              </p>
            )}
          </div>

          {/* Seção inferior sempre no final */}
          <div className={classes.bottomSection}>
            {/* Collapse para links secundários e currículo */}
            {showAllLinks && !summary && (
              <DocumentsCollapse
                className="person-card__docs mb-2x"
                secondary={secondary}
                curriculumFile={person.file}
                messages={messages}
                intl={intl}
                isEditMode={isEditMode}
              />
            )}
            {!summary && showSubstitute && (
              <Substitute
                className="person-card__substitute"
                person={person}
                isEditMode={isEditMode}
              />
            )}
          </div>
        </div>
      </div>
    </article>
  );
};
